export interface PredefinedOption {
  key: string;
  label: string;
  description?: string;
}

export interface OptionTypeMeta {
  label: string;
  description: string;
  allowedFields: ('string_value' | 'numeric_value' | 'legal_code')[];
  defaultScope: 'company' | 'system';
  managementMode: 'global' | 'hybrid';
  predefinedOptions?: PredefinedOption[];
}

export const TRANSVERSAL_OPTION_TYPES: string[] = [
  'CATEGORY_OPTION',
  'DISPLAY_OPTION',
  'PRICE_OPTION',
  'UM',
  'ITEM_TYPE',
  'TAX_RATE',
  'TAX',
  'PAYMENT_METHOD',
  'PAYMENT_TERM',
  'CREDIT_NOTE',
  'DEBIT_NOTE',
  'SALE_PLAN',
  'STOCK_OVERRIDE_CONFIG',
  'MOVEMENT_ENTRY_CONFIG',
  'MOVEMENT_EXIT_CONFIG'
];

export const MODULE_OPTION_TYPES_META: Record<string, OptionTypeMeta> = {
  UM: {
    label: 'Unidades de Medida',
    description: 'Formatos y magnitudes físicas para cuantificar inventario y productos (ej. Kilogramo, Litro, Unidad, Metro).',
    allowedFields: ['string_value', 'legal_code'],
    defaultScope: 'company',
    managementMode: 'global'
  },
  TAX_RATE: {
    label: 'Tasas de Impuesto',
    description: 'Tarifas impositivas aplicables a productos en compras y ventas (ej. IVA 19%, IVA 5%, Exento).',
    allowedFields: ['string_value', 'numeric_value', 'legal_code'],
    defaultScope: 'company',
    managementMode: 'global'
  },
  PAYMENT_METHOD: {
    label: 'Métodos de Pago',
    description: 'Medios de pago disponibles para transacciones (Efectivo, Tarjeta de Débito, Transferencia, Crédito).',
    allowedFields: ['string_value', 'legal_code'],
    defaultScope: 'company',
    managementMode: 'global'
  },
  PAYMENT_TERM: {
    label: 'Términos de Pago',
    description: 'Condiciones de vencimiento para facturas de venta y compra (Contado, Crédito 30 días, etc.).',
    allowedFields: ['string_value', 'legal_code'],
    defaultScope: 'company',
    managementMode: 'global'
  },
  ITEM_TYPE: {
    label: 'Tipos de Artículo',
    description: 'Clasificación principal de los ítems del catálogo (Artículo de Inventario, Servicio, Kit).',
    allowedFields: ['string_value'],
    defaultScope: 'company',
    managementMode: 'global'
  },
  CATEGORY_OPTION: {
    label: 'Categorías de Productos',
    description: 'Agrupaciones lógicas para organizar los artículos en el inventario y pos.',
    allowedFields: ['string_value'],
    defaultScope: 'company',
    managementMode: 'hybrid'
  },
  DISPLAY_OPTION: {
    label: 'Presentaciones de Empaque',
    description: 'Unidades de empaque secundario para comercialización (Caja x4, Caja x6, Display x12).',
    allowedFields: ['string_value'],
    defaultScope: 'company',
    managementMode: 'hybrid'
  },
  PRICE_OPTION: {
    label: 'Listas / Tipos de Precio',
    description: 'Niveles de precio parametrizados para la venta (Precio Mayorista, Punto de Venta, Especial).',
    allowedFields: ['string_value'],
    defaultScope: 'company',
    managementMode: 'hybrid'
  },
  MOVEMENT_TYPE_ENTRY: {
    label: 'Motivos de Entrada de Inventario',
    description: 'Orígenes permitidos para registrar ingresos de stock (Compra, Ajuste, Devolución, Producción).',
    allowedFields: ['string_value'],
    defaultScope: 'company',
    managementMode: 'hybrid'
  },
  MOVEMENT_TYPE_EXIT: {
    label: 'Motivos de Salida de Inventario',
    description: 'Causales para salidas de stock (Venta, Pérdida/Mermas, Salida a Producción, Traslado).',
    allowedFields: ['string_value'],
    defaultScope: 'company',
    managementMode: 'hybrid'
  },
  MOVEMENT_ENTRY_CONFIG: {
    label: 'Configuración de Entradas',
    description: 'Reglas del sistema para movimientos de ingreso (ej. Requiere aprobadores de inventario).',
    allowedFields: ['string_value'],
    defaultScope: 'system',
    managementMode: 'global'
  },
  MOVEMENT_EXIT_CONFIG: {
    label: 'Configuración de Salidas',
    description: 'Reglas del sistema para movimientos de salida (ej. Requiere aprobadores de inventario).',
    allowedFields: ['string_value'],
    defaultScope: 'system',
    managementMode: 'global'
  },
  STOCK_OVERRIDE_CONFIG: {
    label: 'Autorizaciones de Inventario',
    description: 'Configuración para permitir operaciones y ventas excepcionales sin inventario registrado.',
    allowedFields: ['string_value', 'numeric_value'],
    defaultScope: 'company',
    managementMode: 'global',
    predefinedOptions: [
      { key: 'ALLOW_NEGATIVE_STOCK', label: 'Permitir ventas y salidas sin stock registrado (con autorización)' }
    ]
  },
  SALE_PLAN: {
    label: 'Planes de Facturación',
    description: 'Planes y niveles de funcionalidad de facturación asignables a las empresas (Plan Completo, Plan Básico).',
    allowedFields: ['string_value'],
    defaultScope: 'system',
    managementMode: 'global',
    predefinedOptions: [
      { key: 'BASIC', label: 'Plan Básico (Facturación Básica)' },
      { key: 'FULL', label: 'Plan Completo (Facturación Electrónica e Integraciones)' }
    ]
  },
  CREDIT_NOTE: {
    label: 'Motivos de Nota Crédito',
    description: 'Conceptos válidos para la emisión de notas crédito (Anulación, Devolución parcial, Descuento).',
    allowedFields: ['string_value', 'legal_code'],
    defaultScope: 'company',
    managementMode: 'global'
  },
  DEBIT_NOTE: {
    label: 'Motivos de Nota Débito',
    description: 'Conceptos válidos para emisión de notas débito (Intereses, Gastos cobrados, Cambio de valor).',
    allowedFields: ['string_value', 'legal_code'],
    defaultScope: 'company',
    managementMode: 'global'
  },
  EVENT_TYPE: {
    label: 'Tipos de Eventos / Agendamiento',
    description: 'Clasificación de eventos para el módulo de agenda y calendario (Cita, Tarea, Agendamiento, Recordatorio).',
    allowedFields: ['string_value'],
    defaultScope: 'company',
    managementMode: 'hybrid'
  },
  TRACEABLE_CATEGORY: {
    label: 'Categorías de Trazabilidad',
    description: 'Clasificación para elementos con seguimiento individual (Animales, Maquinaria, Vehículos, Activos, Lotes).',
    allowedFields: ['string_value'],
    defaultScope: 'company',
    managementMode: 'hybrid',
    predefinedOptions: [
      { key: 'ANIMAL', label: 'Animal Individual' },
      { key: 'EQUIPMENT', label: 'Equipo / Maquinaria' },
      { key: 'VEHICLE', label: 'Vehículo' },
      { key: 'BATCH', label: 'Lote de Producción' },
      { key: 'ASSET', label: 'Activo Fijo' },
      { key: 'CUSTOM', label: 'Personalizado' }
    ]
  },
  TRACEABLE_LOG_TYPE: {
    label: 'Tipos de Eventos de Trazabilidad',
    description: 'Catálogo de eventos y bitácoras de seguimiento (Mediciones, Vacunaciones, Mantenimientos, Depreciación).',
    allowedFields: ['string_value'],
    defaultScope: 'company',
    managementMode: 'hybrid',
    predefinedOptions: [
      { key: 'OBSERVATION', label: 'Observación' },
      { key: 'MEASUREMENT', label: 'Medición (peso, temperatura, etc.)' },
      { key: 'TREATMENT', label: 'Tratamiento / Medicamento' },
      { key: 'VACCINATION', label: 'Vacunación' },
      { key: 'MAINTENANCE', label: 'Mantenimiento' },
      { key: 'INSPECTION', label: 'Inspección / Control de Calidad' },
      { key: 'TRANSFER', label: 'Traslado / Reubicación' },
      { key: 'STATUS_CHANGE', label: 'Cambio de Estado' },
      { key: 'ASSIGNMENT', label: 'Asignación a persona / área' },
      { key: 'DEPRECIATION', label: 'Depreciación' },
      { key: 'CUSTOM', label: 'Otro' }
    ]
  }
};

export const getOptionTypeMeta = (type: string): OptionTypeMeta => {
  if (MODULE_OPTION_TYPES_META[type]) {
    return MODULE_OPTION_TYPES_META[type];
  }
  return {
    label: type.replaceAll('_', ' '),
    description: `Parámetros configurables de tipo ${type}`,
    allowedFields: ['string_value', 'numeric_value', 'legal_code'],
    defaultScope: 'company',
    managementMode: 'hybrid'
  };
};
